import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import api, { formatINR, formatDate } from "@/lib/api";
import { IndianRupee, ShoppingBag, Users, Clock, TrendingUp, CalendarClock, CheckCircle2 } from "lucide-react";

const CARD_ICONS = {
  today_sales: { icon: IndianRupee, color: "bg-blue-50 text-[#2563EB]", label: "Today's Sales", fmt: formatINR },
  week_sales: { icon: TrendingUp, color: "bg-emerald-50 text-emerald-600", label: "This Week", fmt: formatINR },
  month_sales: { icon: TrendingUp, color: "bg-orange-50 text-[#F97316]", label: "This Month", fmt: formatINR },
  year_sales: { icon: TrendingUp, color: "bg-violet-50 text-violet-600", label: "This Year", fmt: formatINR },
  pending_orders: { icon: Clock, color: "bg-orange-50 text-[#F97316]", label: "Pending Orders" },
  completed_orders: { icon: CheckCircle2, color: "bg-emerald-50 text-emerald-600", label: "Completed" },
  total_customers: { icon: Users, color: "bg-blue-50 text-[#2563EB]", label: "Customers" },
  new_bookings: { icon: CalendarClock, color: "bg-red-50 text-red-500", label: "New Pickups" },
};

const PIE_COLORS = ["#2563EB", "#10B981", "#F97316", "#8B5CF6", "#EF4444", "#0EA5E9", "#F59E0B"];

const KpiCard = ({ id, value, i }) => {
  const meta = CARD_ICONS[id];
  const Icon = meta.icon;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="qw-dash-card p-5" data-testid={`kpi-${id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">{meta.label}</div>
          <div className="mt-2 text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>{meta.fmt ? meta.fmt(value) : value}</div>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.color}`}><Icon size={20}/></div>
      </div>
    </motion.div>
  );
};

const StatusBadge = ({ s }) => {
  const map = {
    Delivered: "qw-badge-green",
    Ready: "qw-badge-green",
    "Out for Delivery": "qw-badge-blue",
    Received: "qw-badge-blue",
    Processing: "qw-badge-orange",
    "Dry Cleaning": "qw-badge-orange",
    Ironing: "qw-badge-orange",
  };
  return <span className={map[s] || "qw-badge-slate"}>{s}</span>;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-slate-100 animate-pulse"/>)}</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Dashboard</h1>
        <p className="text-sm text-slate-500">Real-time overview of your Quick Wash business.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard id="today_sales" value={stats.today_sales} i={0}/>
        <KpiCard id="week_sales" value={stats.week_sales} i={1}/>
        <KpiCard id="month_sales" value={stats.month_sales} i={2}/>
        <KpiCard id="year_sales" value={stats.year_sales} i={3}/>
        <KpiCard id="pending_orders" value={stats.pending_orders} i={4}/>
        <KpiCard id="completed_orders" value={stats.completed_orders} i={5}/>
        <KpiCard id="total_customers" value={stats.total_customers} i={6}/>
        <KpiCard id="new_bookings" value={stats.new_bookings} i={7}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="qw-dash-card p-6 lg:col-span-2" data-testid="chart-sales">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Sales · Last 7 days</div>
              <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>Daily Revenue</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.daily_series}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="date" stroke="#94A3B8" tickLine={false} axisLine={false} fontSize={12}/>
              <YAxis stroke="#94A3B8" tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => `₹${v}`}/>
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} formatter={(v) => formatINR(v)}/>
              <Line type="monotone" dataKey="sales" stroke="#2563EB" strokeWidth={3} dot={{ r: 4, fill: '#2563EB' }} activeDot={{ r: 6 }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="qw-dash-card p-6" data-testid="chart-status">
          <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Order Status</div>
          <div className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>Distribution</div>
          {stats.status_distribution.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No orders yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={stats.status_distribution} dataKey="count" nameKey="status" innerRadius={50} outerRadius={90}>
                  {stats.status_distribution.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="qw-dash-card p-6 lg:col-span-2" data-testid="recent-orders">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>Recent Orders</div>
            <Link to="/admin/bills" className="text-sm text-blue-600 hover:underline">View all →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="text-left py-2">Bill</th><th className="text-left py-2">Customer</th><th className="text-left py-2">Status</th><th className="text-right py-2">Amount</th></tr>
              </thead>
              <tbody>
                {stats.recent_orders.length === 0 ? (
                  <tr><td colSpan="4" className="py-8 text-center text-slate-400">No orders yet. <Link to="/admin/bills/new" className="text-blue-600">Create first bill →</Link></td></tr>
                ) : stats.recent_orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3"><Link to={`/admin/bills/${o.id}`} className="font-semibold text-slate-900 hover:text-blue-600">{o.bill_number}</Link></td>
                    <td className="py-3"><div className="text-slate-900">{o.customer_name}</div><div className="text-xs text-slate-500">{o.mobile}</div></td>
                    <td className="py-3"><StatusBadge s={o.status}/></td>
                    <td className="py-3 text-right font-semibold">{formatINR(o.grand_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="qw-dash-card p-6" data-testid="top-customers">
          <div className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>Top Customers</div>
          {stats.top_customers.length === 0 ? (
            <div className="text-slate-400 text-sm">No customers yet.</div>
          ) : (
            <ul className="space-y-3">
              {stats.top_customers.map((c, i) => (
                <li key={c.mobile} className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full text-white flex items-center justify-center font-bold text-sm ${['bg-[#2563EB]','bg-[#10B981]','bg-[#F97316]','bg-violet-500','bg-slate-500'][i]}`}>{c.name.charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.total_orders} orders</div>
                  </div>
                  <div className="text-sm font-semibold">{formatINR(c.total_spending)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
